import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { format as dateFormat } from 'date-fns';
import { appNotify } from 'store/actions';
import { getWithTokenV2 } from 'api/network';

const formatDate = value => value ? dateFormat(new Date(value), 'MM/dd/yyyy hh:mm:ss a') : '';

function getStatus(load) {
  const end = load.completedOn ?? load.failedOn;
  const seconds = end ? Math.round((new Date(end) - new Date(load.createdOn)) / 1000) : null;
  const duration = seconds == null ? '' : ` after ${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  if (load.completedOn) return `Completed on ${formatDate(load.completedOn)}${duration}`;
  if (load.failedOn) return `Failed on ${formatDate(load.failedOn)}${duration}`;
  return 'In progress';
}

// Shows the event log of the selected tasks' load (the context menu only enables "View Load Log" when they share one).
const LoadLogModal = ({
  classes,
  isOpen,
  setIsOpen,
  taskId,
  appNotify
}) => {
  const [load, setLoad] = useState(null);

  useEffect(() => {
    if (!isOpen || taskId == null) return;
    setLoad(null);
    getWithTokenV2(`/api/postAcquisition/tasks/${taskId}/loadLog`)
      .then(setLoad)
      .catch(error => {
        appNotify(error.message, 'error');
        setIsOpen(false);
      });
  }, [isOpen, taskId]);

  // Entries end in a lone \r, which doesn't break a line in the browser.
  const eventLog = (load?.eventLog ?? '').replace(/\r\n?/g, '\n');

  function copyLog() {
    // Same approach as the grid's copy actions. The textarea goes inside the dialog, because MUI's focus trap
    // would pull focus back out of one appended to the body before the copy runs.
    const container = document.querySelector('[role="dialog"]') ?? document.body;
    const dummy = document.createElement('textarea');
    container.appendChild(dummy);
    dummy.value = eventLog;
    dummy.select();
    document.execCommand('copy');
    container.removeChild(dummy);
    appNotify('Load log copied to clipboard.', 'success', { autoHideDuration: 1500 });
  }

  const metadata = load && [
    ['Created By', load.createdBy],
    ['Created On', formatDate(load.createdOn)],
    ['Status', getStatus(load)],
    ['Import ID', load.importId ?? '—'], // Only an import's auto-load has one
    ['Tasks', load.taskCount]
  ];

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth='lg' fullWidth>
      <DialogTitle disableTypography={true}>
        <Typography variant={'h6'}>Load {load ? load.loadId + `${load.loadType === 'Initial' ? ` - ${load.loadType}` : ''}` : ''}</Typography>
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={() => setIsOpen(false)}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent>
        {load
          ? <>
              <div className={classes.metadata}>
                {metadata.map(([label, value]) =>
                  <React.Fragment key={label}>
                    <Typography variant='body2' className={classes.label}>{label}</Typography>
                    <Typography variant='body2'>{value}</Typography>
                  </React.Fragment>
                )}
              </div>
              <pre className={classes.eventLog}>{eventLog || 'No log was recorded for this load.'}</pre>
            </>
          : <div className={classes.loadingWrapper}>
              <CircularProgress />
            </div>
        }
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={copyLog} disabled={!eventLog}>Copy log</Button>
        <Button variant='contained' color='primary' onClick={() => setIsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = theme => ({
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200
  },
  metadata: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: 16,
    rowGap: 4,
    marginBottom: 16
  },
  label: {
    fontWeight: 600
  },
  eventLog: {
    margin: 0,
    padding: 12,
    // What's left of the window after the dialog's margins, title, metadata and buttons, so only the log scrolls
    maxHeight: 'calc(100vh - 450px)',
    minHeight: 150,
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
});

const mapDispatch = {
  appNotify
};

export default compose(
  withStyles(styles),
  connect(null, mapDispatch)
)(LoadLogModal);
